import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';

/**
 * Hook that provides an authenticated fetch function.
 * Automatically acquires an access token from MSAL and attaches it
 * as a Bearer token in the Authorization header.
 */
export function useAuthFetch() {
  const { instance, accounts } = useMsal();

  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      if (accounts.length === 0) {
        // Not authenticated — make a plain fetch (API will return 401 if auth required)
        return fetch(url, options);
      }

      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        const headers = new Headers(options.headers);
        headers.set('Authorization', `Bearer ${tokenResponse.accessToken}`);
        // Also send token via custom header to bypass SWA EasyAuth token replacement
        headers.set('X-MSAL-Token', tokenResponse.accessToken);

        return fetch(url, { ...options, headers });
      } catch (error) {
        console.error('Failed to acquire token for API call:', error);
        // Fall back to unauthenticated fetch
        return fetch(url, options);
      }
    },
    [instance, accounts]
  );

  return authFetch;
}
